/**
 * Trovva — Broker Dashboard (Redesigned)
 *
 * Two-pane command center: Brokerage | Property Management
 * Opens at 7am, know everything in 10 seconds.
 *
 * Layout:
 *   1. Greeting + tab toggle (Brokerage | PM)
 *   2. KPI tiles (4 per pane, contextual)
 *   3. Brokerage: Agent Snapshot + Deals Timeline
 *      PM: Property Overview + Lease Calendar
 *   4. Needs Attention (cross-entity, tagged)
 *   5. Today's Schedule + Recent Activity (2-col)
 */
import { useState, useMemo } from 'react';
import {
  Users, Wrench, FileText, DollarSign, AlertTriangle, Clock,
  TrendingUp, TrendingDown, ChevronRight, Home, Building2,
  Target, Calendar, CheckCircle, Zap, ArrowRight, Activity,
  BarChart3, Key, UserCheck, Bell, Send, Sparkles,
} from 'lucide-react';
import { theme } from '../../lib/theme';
import { TEAM_MEMBERS } from '../../lib/constants';
import TrovvaIcon from '../common/TrovvaIcon';
// OnboardingChecklist removed — replaced by full-screen OnboardingFlow in App.jsx
import IntelligenceCards from './IntelligenceCards';
import LeaderboardWidget from './LeaderboardWidget';
import AttritionWidget from './AttritionWidget';
import EscalationQueue from './EscalationQueue';
import TrendCharts from './TrendCharts';

// ════════════════════════════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════════════════════════════

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function formatCurrency(val) {
  if (typeof val === 'string') return val;
  if (!val) return '$0';
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(1)}K`;
  return `$${val}`;
}

// ════════════════════════════════════════════════════════════════
// KPI TILE
// ════════════════════════════════════════════════════════════════

function KPITile({ label, value, sub, change, Icon, color, onClick }) {
  const tileColor = color || theme.colors.primary;
  return (
    <button onClick={onClick} style={{
      flex: '1 1 0', minWidth: 150, textAlign: 'left', cursor: onClick ? 'pointer' : 'default',
      background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
      borderRadius: 14, padding: '18px 20px', position: 'relative', overflow: 'hidden',
      boxShadow: theme.shadow.sm, transition: 'box-shadow 0.2s, transform 0.2s',
    }}
    onMouseEnter={e => { e.currentTarget.style.boxShadow = theme.shadow.md; e.currentTarget.style.transform = 'translateY(-1px)'; }}
    onMouseLeave={e => { e.currentTarget.style.boxShadow = theme.shadow.sm; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      {/* Bold top accent bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${tileColor}, ${tileColor}88)`,
      }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10, background: `${tileColor}15`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {Icon && <Icon size={17} color={tileColor} />}
        </div>
        {change != null && (
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
            background: change >= 0 ? `${theme.colors.success}15` : `${theme.colors.danger}15`,
            color: change >= 0 ? theme.colors.success : theme.colors.danger,
            display: 'flex', alignItems: 'center', gap: 2,
          }}>
            {change >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
            {change >= 0 ? '+' : ''}{change}%
          </span>
        )}
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color: tileColor, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 500, color: theme.colors.textLight, marginTop: 5 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: theme.colors.textLight, marginTop: 2 }}>{sub}</div>}
    </button>
  );
}

// ════════════════════════════════════════════════════════════════
// AGENT SNAPSHOT (mini table — top agents from Agent Activity data)
// ════════════════════════════════════════════════════════════════

function AgentSnapshot({ agents, setActiveTab }) {
  return (
    <div data-tour="agent-snapshot" style={{
      background: theme.colors.card, borderRadius: 12,
      border: `1px solid ${theme.colors.border}`, overflow: 'hidden', flex: 1,
    }}>
      <div style={{
        padding: '12px 16px', borderBottom: `1px solid ${theme.colors.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Users size={14} color={theme.colors.primary} />
          <span style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>Agent Snapshot</span>
        </div>
        <button onClick={() => setActiveTab?.('activity')} style={{
          fontSize: 11, fontWeight: 600, color: theme.colors.primary,
          background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 3,
        }}>
          View all <ChevronRight size={12} />
        </button>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {['Agent', 'Score', 'Pipeline', 'Overdue'].map(h => (
              <th key={h} style={{
                padding: '8px 12px', fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                color: theme.colors.textLight, letterSpacing: '0.5px',
                textAlign: h === 'Agent' ? 'left' : 'center',
                borderBottom: `1px solid ${theme.colors.border}`, background: theme.colors.bg,
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {agents.map((a, i) => (
            <tr key={i} style={{ borderBottom: i < agents.length - 1 ? `1px solid ${theme.colors.border}40` : 'none' }}>
              <td style={{ padding: '9px 12px', fontSize: 12, fontWeight: 600, color: theme.colors.text }}>
                {a.name}
              </td>
              <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 5,
                  background: `${a.gradeColor}15`, color: a.gradeColor,
                }}>{a.grade}</span>
              </td>
              <td style={{ padding: '9px 12px', fontSize: 12, fontWeight: 600, color: theme.colors.success, textAlign: 'center' }}>
                {a.pipeline}
              </td>
              <td style={{
                padding: '9px 12px', fontSize: 12, fontWeight: 700, textAlign: 'center',
                color: a.overdue > 0 ? theme.colors.danger : theme.colors.success,
              }}>
                {a.overdue}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// DEALS TIMELINE (next milestones across all deals)
// ════════════════════════════════════════════════════════════════

function DealsTimeline({ events, setActiveTab }) {
  return (
    <div data-tour="deals-timeline" style={{
      background: theme.colors.card, borderRadius: 12,
      border: `1px solid ${theme.colors.border}`, overflow: 'hidden', flex: 1,
    }}>
      <div style={{
        padding: '12px 16px', borderBottom: `1px solid ${theme.colors.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Target size={14} color={theme.colors.accent} />
          <span style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>Deals Timeline</span>
        </div>
        <button onClick={() => setActiveTab?.('transactions')} style={{
          fontSize: 11, fontWeight: 600, color: theme.colors.primary,
          background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 3,
        }}>
          All deals <ChevronRight size={12} />
        </button>
      </div>
      {/* Header row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px',
        background: theme.colors.bg, borderBottom: `1px solid ${theme.colors.border}`,
      }}>
        <div style={{ width: 8 }} />
        {['Date', 'Milestone', ''].map((h, idx) => (
          <div key={h || idx} style={{
            fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
            color: theme.colors.textLight,
            minWidth: h === 'Date' ? 52 : undefined,
            flex: h === 'Milestone' ? 1 : undefined,
            flexShrink: h === 'Date' ? 0 : undefined,
          }}>{h}</div>
        ))}
      </div>
      <div style={{ padding: '0' }}>
        {events.length === 0 ? (
          <div style={{ padding: '28px 20px', textAlign: 'center' }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: `${theme.colors.primary}10`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 10px',
            }}>
              <FileText size={20} color={theme.colors.primary} />
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text, marginBottom: 4 }}>No upcoming milestones</div>
            <div style={{ fontSize: 12, color: theme.colors.textLight }}>Deal milestones will appear here as transactions progress.</div>
          </div>
        ) : events.map((e, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
            borderBottom: i < events.length - 1 ? `1px solid ${theme.colors.border}40` : 'none',
          }}>
            <div style={{
              width: 8, height: 8, borderRadius: 4, flexShrink: 0,
              background: e.urgent ? theme.colors.danger : e.soon ? theme.colors.warning : theme.colors.primary,
            }} />
            <div style={{
              fontSize: 11, fontWeight: 700, color: theme.colors.primary,
              minWidth: 52, flexShrink: 0,
            }}>
              {e.date}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text }}>{e.title}</div>
              <div style={{ fontSize: 10, color: theme.colors.textLight }}>{e.subtitle}</div>
            </div>
            {e.badge && (
              <span style={{
                fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 5, flexShrink: 0,
                background: `${e.badgeColor || theme.colors.primary}12`,
                color: e.badgeColor || theme.colors.primary,
              }}>{e.badge}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// PROPERTY OVERVIEW (mini table — PM properties)
// ════════════════════════════════════════════════════════════════

function PropertyOverview({ properties, setActiveTab }) {
  return (
    <div style={{
      background: theme.colors.card, borderRadius: 12,
      border: `1px solid ${theme.colors.border}`, overflow: 'hidden', flex: 1,
    }}>
      <div style={{
        padding: '12px 16px', borderBottom: `1px solid ${theme.colors.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Building2 size={14} color={theme.colors.info} />
          <span style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>Properties</span>
        </div>
        <button onClick={() => setActiveTab?.('pm')} style={{
          fontSize: 11, fontWeight: 600, color: theme.colors.primary,
          background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 3,
        }}>
          Manage <ChevronRight size={12} />
        </button>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {['Property', 'Units', 'Occupancy', 'Rent/Mo'].map(h => (
              <th key={h} style={{
                padding: '8px 12px', fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                color: theme.colors.textLight, letterSpacing: '0.5px',
                textAlign: h === 'Property' ? 'left' : 'center',
                borderBottom: `1px solid ${theme.colors.border}`, background: theme.colors.bg,
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {properties.map((p, i) => (
            <tr key={i} style={{ borderBottom: i < properties.length - 1 ? `1px solid ${theme.colors.border}40` : 'none' }}>
              <td style={{ padding: '9px 12px', fontSize: 12, fontWeight: 600, color: theme.colors.text }}>{p.address}</td>
              <td style={{ padding: '9px 12px', fontSize: 12, textAlign: 'center', color: theme.colors.text }}>{p.units}</td>
              <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  color: p.occupancy >= 90 ? theme.colors.success : p.occupancy >= 75 ? theme.colors.warning : theme.colors.danger,
                }}>{p.occupancy}%</span>
              </td>
              <td style={{ padding: '9px 12px', fontSize: 12, fontWeight: 600, color: theme.colors.success, textAlign: 'center' }}>
                {formatCurrency(p.rent)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// LEASE CALENDAR (upcoming renewals/expirations)
// ════════════════════════════════════════════════════════════════

function LeaseCalendar({ leases, setActiveTab }) {
  return (
    <div style={{
      background: theme.colors.card, borderRadius: 12,
      border: `1px solid ${theme.colors.border}`, overflow: 'hidden', flex: 1,
    }}>
      <div style={{
        padding: '12px 16px', borderBottom: `1px solid ${theme.colors.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Key size={14} color={theme.colors.warning} />
          <span style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>Lease Renewals</span>
        </div>
      </div>
      {/* Header row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px',
        background: theme.colors.bg, borderBottom: `1px solid ${theme.colors.border}`,
      }}>
        <div style={{ width: 8 }} />
        {['Tenant / Property', 'Days Left'].map(h => (
          <div key={h} style={{
            fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
            color: theme.colors.textLight,
            flex: h === 'Tenant / Property' ? 1 : undefined,
            flexShrink: h === 'Days Left' ? 0 : undefined,
          }}>{h}</div>
        ))}
      </div>
      <div style={{ padding: '0' }}>
        {leases.length === 0 ? (
          <div style={{ padding: '28px 20px', textAlign: 'center' }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: `${theme.colors.info}10`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 10px',
            }}>
              <Key size={20} color={theme.colors.info} />
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text, marginBottom: 4 }}>No upcoming renewals</div>
            <div style={{ fontSize: 12, color: theme.colors.textLight }}>Lease renewal dates will appear here as they approach.</div>
          </div>
        ) : leases.map((l, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
            borderBottom: i < leases.length - 1 ? `1px solid ${theme.colors.border}40` : 'none',
          }}>
            <div style={{
              width: 8, height: 8, borderRadius: 4, flexShrink: 0,
              background: l.daysLeft <= 14 ? theme.colors.danger : l.daysLeft <= 30 ? theme.colors.warning : theme.colors.info,
            }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text }}>{l.tenant} — {l.property}</div>
              <div style={{ fontSize: 10, color: theme.colors.textLight }}>{l.unit} • Expires {l.expiry}</div>
            </div>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 5,
              background: l.daysLeft <= 14 ? `${theme.colors.danger}12` : l.daysLeft <= 30 ? `${theme.colors.warning}12` : `${theme.colors.info}12`,
              color: l.daysLeft <= 14 ? theme.colors.danger : l.daysLeft <= 30 ? theme.colors.warning : theme.colors.info,
            }}>{l.daysLeft}d</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// NEEDS ATTENTION — split by Brokerage / PM
// ════════════════════════════════════════════════════════════════

function AttentionSection({ label, Icon, iconColor, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{
      background: theme.colors.card, borderRadius: 12,
      border: `1px solid ${theme.colors.border}`, overflow: 'hidden',
    }}>
      <div style={{
        padding: '10px 16px', borderBottom: `1px solid ${theme.colors.border}`,
        display: 'flex', alignItems: 'center', gap: 6, background: theme.colors.bg,
      }}>
        <Icon size={13} color={iconColor} />
        <span style={{ fontSize: 12, fontWeight: 700, color: theme.colors.text }}>{label}</span>
        <span style={{
          fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 5,
          background: `${theme.colors.danger}12`, color: theme.colors.danger, marginLeft: 4,
        }}>{items.length}</span>
      </div>
      {items.map((item, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px',
          borderBottom: i < items.length - 1 ? `1px solid ${theme.colors.border}40` : 'none',
          cursor: 'pointer',
        }}>
          <div style={{
            width: 8, height: 8, borderRadius: 4, flexShrink: 0,
            background: item.color || theme.colors.warning,
          }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text }}>{item.title}</div>
            <div style={{ fontSize: 10, color: theme.colors.textLight }}>{item.subtitle}</div>
          </div>
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 5, flexShrink: 0,
            background: `${item.color || theme.colors.warning}12`,
            color: item.color || theme.colors.warning,
          }}>{item.badge}</span>
        </div>
      ))}
    </div>
  );
}

function NeedsAttention({ items }) {
  const brokerage = (items || []).filter(i => i.entity !== 'PM');
  const pm = (items || []).filter(i => i.entity === 'PM');

  if (brokerage.length === 0 && pm.length === 0) {
    return (
      <div style={{
        padding: '14px 18px', borderRadius: 12, marginBottom: 16,
        background: `${theme.colors.success}06`, border: `1px solid ${theme.colors.success}20`,
        fontSize: 12, color: theme.colors.success, fontWeight: 600,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <CheckCircle size={14} /> All caught up — nothing needs attention right now.
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <AlertTriangle size={14} color={theme.colors.accent} />
        <span style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>Needs Your Attention</span>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 5,
          background: `${theme.colors.danger}12`, color: theme.colors.danger, marginLeft: 4,
        }}>{(items || []).length}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 12 }}>
        <AttentionSection label="Brokerage" Icon={Home} iconColor={theme.colors.primary} items={brokerage} />
        <AttentionSection label="Property Management" Icon={Building2} iconColor={theme.colors.info} items={pm} />
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// DEMO DATA (hardcoded, ready to swap for real Supabase queries)
// ════════════════════════════════════════════════════════════════

const DEMO_BROKERAGE = {
  companyDollar: 18400,
  companyDollarChange: 12,
  pipeline: 5310000,
  pipelineDeals: 18,
  activeDeals: 6,
  closingSoon: 2,
  newLeads: 3,
  newLeadsWeek: 8,
  avgSpeedToLead: 14, // minutes
  agents: [
    { name: 'Patricia Furrow',   grade: 'B', gradeColor: '#4CAF50', pipeline: '$1.8M', overdue: 10 },
    { name: 'Sylvia Micarelli',  grade: 'D', gradeColor: '#E08B3B', pipeline: '$1.3M', overdue: 14 },
    { name: 'Jenifer Raffa',     grade: 'D', gradeColor: '#E08B3B', pipeline: '$875K', overdue: 14 },
    { name: 'Desiree Hogbin',    grade: 'D', gradeColor: '#E08B3B', pipeline: '$620K', overdue: 6 },
    { name: 'Neha',              grade: 'D', gradeColor: '#E08B3B', pipeline: '$340K', overdue: 8 },
  ],
  dealsTimeline: [
    { date: 'Apr 22', title: 'Closing — 456 Cherry Hill Rd', subtitle: 'Sylvia M. • Buyer side', badge: '4 DAYS', badgeColor: theme.colors.warning, soon: true },
    { date: 'Apr 25', title: 'Inspection — 1010 Route 70', subtitle: 'Patricia F. • Commercial', badge: '7 DAYS', badgeColor: theme.colors.info },
    { date: 'Apr 28', title: 'Attorney Review ends', subtitle: '789 Washington Ave • Johanna M.' },
    { date: 'May 3', title: 'Mortgage Commitment due', subtitle: 'Sarah Mitchell • Buyer' },
    { date: 'May 10', title: 'Closing — 55 Broad St', subtitle: 'Jenifer R. • Seller side' },
  ],
};

const DEMO_PM = {
  totalUnits: 12,
  occupiedUnits: 10,
  occupancyRate: 83,
  rentMTD: 13200,
  rentExpected: 16000,
  openMaintenance: 4,
  urgentMaintenance: 1,
  renewalsExpiring: 3,
  renewalsExpiring30d: 2,
  properties: [
    { address: '142 Oak St', units: 4, occupancy: 100, rent: 6800 },
    { address: '85 Maple Ave', units: 3, occupancy: 100, rent: 5400 },
    { address: '302 Elm St', units: 1, occupancy: 100, rent: 2200 },
    { address: '15 Cedar Lane', units: 4, occupancy: 75, rent: 5400 },
  ],
  leases: [
    { tenant: 'Amy Patel', property: '15 Cedar Lane', unit: 'Unit 1', expiry: 'May 5', daysLeft: 17 },
    { tenant: 'Stephanie Brown', property: '15 Cedar Lane', unit: 'Unit 2', expiry: 'May 5', daysLeft: 17 },
    { tenant: 'Emily Watson', property: '85 Maple Ave', unit: 'Unit 3', expiry: 'Jul 15', daysLeft: 88 },
    { tenant: 'Carlos Rivera', property: '302 Elm St', unit: 'Main', expiry: 'Oct 31', daysLeft: 196 },
  ],
};

const DEMO_ATTENTION = [
  { title: 'No hot water — 302 Elm Street', subtitle: 'Carlos Rivera • 20 min ago', badge: 'URGENT', color: theme.colors.danger, entity: 'PM' },
  { title: 'Counter offer due tomorrow — 1010 Route 70', subtitle: 'NJ Dental Group • Patricia Furrow', badge: '1 DAY', color: theme.colors.warning, entity: 'Brokerage' },
  { title: 'New seller lead — Maria Rodriguez', subtitle: 'Inherited property, wants quick sale', badge: 'NEW', color: theme.colors.info, entity: 'Brokerage' },
  { title: 'Lease expires in 17 days — 15 Cedar Lane', subtitle: 'Amy Patel & Stephanie Brown • Negotiating renewal', badge: '17 DAYS', color: theme.colors.accent, entity: 'PM' },
];

const DEMO_SCHEDULE = [
  { time: '9:00 AM', agent: 'Govind Padiath', title: 'Team huddle — weekly pipeline review', subtitle: 'All agents • Zoom' },
  { time: '10:00 AM', agent: 'Johanna Micarelli', title: 'Showing — 789 Washington Ave', subtitle: 'Seller walkthrough with buyer' },
  { time: '11:30 AM', agent: 'Sylvia Micarelli', title: 'Buyer consultation', subtitle: 'Sarah Mitchell • Pre-approval review' },
  { time: '1:00 PM', agent: 'Jenifer Raffa', title: 'Listing appointment', subtitle: '55 Broad St — new seller lead' },
  { time: '2:00 PM', agent: 'Patricia Furrow', title: 'Inspection — 1010 Route 70', subtitle: 'NJ Dental Group • Commercial' },
  { time: '3:30 PM', agent: 'Govind Padiath', title: 'Vendor meeting — ComfortAir', subtitle: '85 Maple Ave HVAC quote review' },
];

const DEMO_ACTIVITY = [
  { action: 'Auto-responded to Sarah Mitchell (new buyer lead)', time: '2 hours ago', color: theme.colors.info },
  { action: 'Dispatched ComfortAir HVAC to 85 Maple Ave', time: '30 min ago', color: theme.colors.success },
  { action: 'Flagged lease renewal — 15 Cedar Lane (17 days)', time: '1 hour ago', color: theme.colors.warning },
  { action: 'Sent multi-quote request for 142 Oak St faucet', time: '45 min ago', color: theme.colors.info },
  { action: 'Patricia Furrow completed 3 follow-up tasks', time: '3 hours ago', color: theme.colors.success },
];

// ════════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ════════════════════════════════════════════════════════════════

const BRAIN_HERO_PROMPTS = [
  { label: "What's going on today?", icon: '📋' },
  { label: "Any compliance deadlines?", icon: '⚠️' },
  { label: "Who needs follow-up?", icon: '📞' },
  { label: "How is my team doing?", icon: '👥' },
  { label: "Pipeline summary", icon: '📊' },
  { label: "What's overdue?", icon: '🔴' },
];

export default function DashboardHome({ stats, setActiveTab, demoMode, onBrainOpen, user, org }) {
  const [pane, setPane] = useState('brokerage'); // 'brokerage' | 'pm'
  const [heroInput, setHeroInput] = useState('');

  const bk = DEMO_BROKERAGE;
  const pm = DEMO_PM;

  const handleHeroSend = (text) => {
    const q = (text || heroInput).trim();
    if (!q) return;
    setHeroInput('');
    onBrainOpen?.(q);
  };

  return (
    <div style={{ padding: '16px 22px', maxWidth: 1200, margin: '0 auto' }}>

      {/* ══════════ WELCOME HERO — Brain front door (compact) ══════════ */}
      <div data-tour="brain-hero" style={{
        background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryDark})`,
        borderRadius: 14, padding: '16px 20px', marginBottom: 16,
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Subtle decorative circles */}
        <div style={{
          position: 'absolute', top: -40, right: -40, width: 120, height: 120,
          borderRadius: '50%', background: 'rgba(255,255,255,0.04)',
        }} />

        {/* Brain header + input in one row on desktop */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, position: 'relative' }}>
          <div style={{
            width: 36, height: 36, borderRadius: 9, flexShrink: 0,
            background: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <TrovvaIcon size={20} color="white" animate />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'white', lineHeight: 1.2, marginBottom: 6 }}>
              Trovva Brain
              <span style={{ fontSize: 11, fontWeight: 400, color: 'rgba(255,255,255,0.6)', marginLeft: 8 }}>
                Ask about deals, leads, compliance, or anything.
              </span>
            </div>
            {/* Input bar */}
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="text"
                value={heroInput}
                onChange={e => setHeroInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleHeroSend(); }}
                placeholder="Ask Trovva anything..."
                style={{
                  flex: 1, padding: '9px 14px', borderRadius: 10,
                  border: '1.5px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.1)',
                  color: 'white', fontSize: 13, outline: 'none',
                  fontFamily: 'inherit',
                  backdropFilter: 'blur(8px)',
                }}
              />
              <button
                onClick={() => handleHeroSend()}
                disabled={!heroInput.trim()}
                style={{
                  width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                  background: heroInput.trim() ? theme.colors.accent : 'rgba(255,255,255,0.15)',
                  color: 'white', border: 'none', cursor: heroInput.trim() ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.2s',
                }}
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Suggested prompts — compact chips */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 10, position: 'relative', paddingLeft: 50 }}>
          {BRAIN_HERO_PROMPTS.map((p, i) => (
            <button
              key={i}
              onClick={() => handleHeroSend(p.label)}
              style={{
                padding: '5px 11px', borderRadius: 16,
                border: '1px solid rgba(255,255,255,0.18)',
                background: 'rgba(255,255,255,0.07)',
                color: 'rgba(255,255,255,0.85)', cursor: 'pointer',
                fontSize: 11, fontWeight: 500,
                display: 'flex', alignItems: 'center', gap: 4,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'; }}
            >
              <span style={{ fontSize: 11 }}>{p.icon}</span> {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* ══════════ INTELLIGENCE CARDS ══════════ */}
      <IntelligenceCards demoMode={demoMode} />

      {/* ── Header: Pane toggle + Quick actions ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <div>
          {/* Pane toggle */}
          <div data-tour="pane-toggle" style={{ display: 'flex', borderRadius: 10, overflow: 'hidden', border: `1px solid ${theme.colors.border}`, marginTop: 8 }}>
            {[
              { id: 'brokerage', label: 'Brokerage', Icon: Home },
              { id: 'pm', label: 'Property Mgmt', Icon: Building2 },
            ].map(p => (
              <button key={p.id} onClick={() => setPane(p.id)} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 18px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                background: pane === p.id ? theme.colors.primary : theme.colors.card,
                color: pane === p.id ? 'white' : theme.colors.text,
                transition: 'all .15s',
              }}>
                <p.Icon size={13} /> {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ════════════ BROKERAGE PANE ════════════ */}
      {pane === 'brokerage' && (
        <>
          {/* KPI Row */}
          <div data-tour="kpi-tiles" style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
            <KPITile
              Icon={DollarSign} label="Company Dollar MTD" color={theme.colors.success}
              value={formatCurrency(bk.companyDollar)} change={bk.companyDollarChange}
              onClick={() => setActiveTab?.('financials')}
            />
            <KPITile
              Icon={BarChart3} label="Pipeline Value" color={theme.colors.primary}
              value={formatCurrency(bk.pipeline)}
              sub={`${bk.pipelineDeals} deals in pipeline`}
              onClick={() => setActiveTab?.('transactions')}
            />
            <KPITile
              Icon={FileText} label="Active Deals" color={theme.colors.accent}
              value={bk.activeDeals}
              sub={`${bk.closingSoon} closing this month`}
              onClick={() => setActiveTab?.('transactions')}
            />
            <KPITile
              Icon={Users} label="New Leads This Week" color={theme.colors.info}
              value={bk.newLeadsWeek}
              sub={`Avg response: ${bk.avgSpeedToLead}m`}
              onClick={() => setActiveTab?.('leads')}
            />
          </div>

          {/* Agent Snapshot + Deals Timeline */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14, marginBottom: 18 }}>
            <AgentSnapshot agents={bk.agents} setActiveTab={setActiveTab} />
            <DealsTimeline events={bk.dealsTimeline} setActiveTab={setActiveTab} />
          </div>

          {/* Leaderboard Widget */}
          <LeaderboardWidget agents={bk.agents} setActiveTab={setActiveTab} />

          {/* Performance Trend Charts */}
          <div style={{ marginTop: 18 }}>
            <TrendCharts demoMode={demoMode} />
          </div>
        </>
      )}

      {/* ════════════ PROPERTY MANAGEMENT PANE ════════════ */}
      {pane === 'pm' && (
        <>
          {/* KPI Row */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
            <KPITile
              Icon={Building2} label="Occupancy" color={pm.occupancyRate >= 90 ? theme.colors.success : theme.colors.warning}
              value={`${pm.occupiedUnits}/${pm.totalUnits}`}
              sub={`${pm.occupancyRate}% occupied`}
              onClick={() => setActiveTab?.('pm')}
            />
            <KPITile
              Icon={DollarSign} label="Rent Collected MTD" color={theme.colors.success}
              value={formatCurrency(pm.rentMTD)}
              sub={`of ${formatCurrency(pm.rentExpected)} expected`}
              onClick={() => setActiveTab?.('financials')}
            />
            <KPITile
              Icon={Wrench} label="Open Maintenance" color={pm.urgentMaintenance > 0 ? theme.colors.danger : theme.colors.warning}
              value={pm.openMaintenance}
              sub={pm.urgentMaintenance > 0 ? `${pm.urgentMaintenance} urgent` : 'None urgent'}
              onClick={() => setActiveTab?.('maintenance')}
            />
            <KPITile
              Icon={Key} label="Renewals Expiring" color={theme.colors.accent}
              value={pm.renewalsExpiring}
              sub={`${pm.renewalsExpiring30d} within 30 days`}
            />
          </div>

          {/* Property Overview + Lease Calendar */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14, marginBottom: 18 }}>
            <PropertyOverview properties={pm.properties} setActiveTab={setActiveTab} />
            <LeaseCalendar leases={pm.leases} setActiveTab={setActiveTab} />
          </div>
        </>
      )}

      {/* ════════════ CROSS-ENTITY: NEEDS ATTENTION ════════════ */}
      <div data-tour="attention">
        <NeedsAttention items={DEMO_ATTENTION} />
      </div>

      {/* ════════════ SCHEDULE + ACTIVITY (2-col) ════════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14, marginBottom: 18 }}>
        {/* Today's Schedule */}
        <div data-tour="schedule" style={{
          background: theme.colors.card, borderRadius: 12,
          border: `1px solid ${theme.colors.border}`, overflow: 'hidden',
        }}>
          <div style={{
            padding: '12px 16px', borderBottom: `1px solid ${theme.colors.border}`,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Clock size={14} color={theme.colors.primary} />
            <span style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>Today's Schedule</span>
            <span style={{ fontSize: 10, color: theme.colors.textLight, marginLeft: 'auto' }}>{DEMO_SCHEDULE.length} events</span>
          </div>
          {DEMO_SCHEDULE.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: theme.colors.textLight, fontSize: 12 }}>Clear day!</div>
          ) : (
            <div style={{ padding: '4px 0' }}>
              {DEMO_SCHEDULE.map((item, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                  borderBottom: i < DEMO_SCHEDULE.length - 1 ? `1px solid ${theme.colors.border}40` : 'none',
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: theme.colors.primary, minWidth: 60, flexShrink: 0 }}>{item.time}</div>
                  <div style={{
                    fontSize: 11, fontWeight: 600, color: theme.colors.text, minWidth: 120, flexShrink: 0,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {item.agent || ''}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text }}>{item.title}</div>
                    <div style={{ fontSize: 10, color: theme.colors.textLight }}>{item.subtitle}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div data-tour="activity" style={{
          background: theme.colors.card, borderRadius: 12,
          border: `1px solid ${theme.colors.border}`, overflow: 'hidden',
        }}>
          <div style={{
            padding: '12px 16px', borderBottom: `1px solid ${theme.colors.border}`,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Activity size={14} color={theme.colors.accent} />
            <span style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>Recent Activity</span>
          </div>
          <div style={{ padding: '4px 0' }}>
            {DEMO_ACTIVITY.map((item, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 16px',
                borderBottom: i < DEMO_ACTIVITY.length - 1 ? `1px solid ${theme.colors.border}40` : 'none',
              }}>
                <div style={{
                  width: 7, height: 7, borderRadius: 4, marginTop: 5, flexShrink: 0,
                  background: item.color || theme.colors.info,
                }} />
                <div>
                  <div style={{ fontSize: 12, color: theme.colors.text }}>{item.action}</div>
                  <div style={{ fontSize: 10, color: theme.colors.textLight }}>{item.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ════════════ ESCALATIONS + ATTRITION (broker only) ════════════ */}
      {(pane === 'brokerage') && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14, marginTop: 14 }}>
          <EscalationQueue user={user} compact />
          <AttritionWidget user={user} org={org} />
        </div>
      )}

    </div>
  );
}
